import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Settings as SettingsIcon,
  AlertTriangle,
  Shield,
  Target,
  Bell,
  RefreshCw,
  Save,
  Info,
  Mail,
  Clock,
  Send,
  ExternalLink,
  Loader2,
  CheckCircle,
  TrendingUp,
  Trash2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const riskProfiles = [
  { value: "conservative", label: "Conservative", description: "Lower risk, stable returns" },
  { value: "moderate", label: "Moderate", description: "Balanced risk and reward" },
  { value: "moderate_aggressive", label: "Moderate Aggressive", description: "Higher growth potential" },
  { value: "aggressive", label: "Aggressive", description: "Maximum growth, higher volatility" },
];

export const Settings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [settings, setSettings] = useState({
    is_paper_trading: true,
    risk_profile: "moderate_aggressive",
    starting_balance: 10000,
    target_roi: 20,
    auto_rebalance: false,
    notifications_enabled: true,
  });
  
  // Email briefing settings
  const [briefingSettings, setBriefingSettings] = useState({
    enabled: true,
    recipient_email: "",
    send_time_utc: 7
  });
  
  // Price alert settings
  const [alertSettings, setAlertSettings] = useState({
    surge_threshold: 5.0,
    check_interval: 60,
    monitored_symbols: 0
  });
  const [clearingAlerts, setClearingAlerts] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchBriefingSettings();
    fetchAlertSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchBriefingSettings = async () => {
    try {
      const response = await axios.get(`${API}/briefing/settings`);
      setBriefingSettings({
        enabled: response.data.enabled,
        recipient_email: response.data.recipient_email || "",
        send_time_utc: response.data.send_time_utc || 7
      });
    } catch (error) {
      console.error("Error fetching briefing settings:", error);
    }
  };
  
  const fetchAlertSettings = async () => {
    try {
      const response = await axios.get(`${API}/alerts/settings`);
      setAlertSettings({
        surge_threshold: response.data.surge_threshold || 5.0,
        check_interval: response.data.check_interval || 60,
        monitored_symbols: response.data.monitored_symbols || 0
      });
    } catch (error) {
      console.error("Error fetching alert settings:", error);
    }
  };
  
  const updateAlertSettings = async (threshold) => {
    try {
      await axios.put(`${API}/alerts/settings?threshold=${threshold}&interval=${alertSettings.check_interval}`);
      setAlertSettings(prev => ({ ...prev, surge_threshold: threshold }));
      toast.success(`Alert threshold updated to ±${threshold}%`);
    } catch (error) {
      toast.error("Failed to update alert settings");
    }
  };
  
  const clearAllAlerts = async () => {
    setClearingAlerts(true);
    try {
      await axios.delete(`${API}/alerts`);
      toast.success("All alerts cleared");
    } catch (error) {
      toast.error("Failed to clear alerts");
    } finally {
      setClearingAlerts(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings);
      await axios.put(`${API}/briefing/settings`, briefingSettings);
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
      console.error("Error saving settings:", error);
    } finally {
      setSaving(false);
    }
  };
  
  const sendTestBriefing = async () => {
    if (!briefingSettings.recipient_email) {
      toast.error("Please enter an email address first");
      return;
    }
    
    setSendingTest(true);
    try {
      await axios.post(`${API}/briefing/send-test?email=${briefingSettings.recipient_email}`);
      toast.success(`Test briefing sent to ${briefingSettings.recipient_email}`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to send test email";
      toast.error(errorMsg);
    } finally {
      setSendingTest(false);
    }
  };
  
  const previewBriefing = () => {
    window.open(`${API}/briefing/preview`, '_blank');
  };

  const handleChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const resetPortfolio = async () => {
    if (!window.confirm("Are you sure you want to reset your portfolio? This will clear all holdings and trade history.")) {
      return;
    }
    // In a real app, this would call an API endpoint
    toast.success("Portfolio reset successfully");
    navigate("/");
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-6" data-testid="settings-loading">
        <Skeleton className="h-8 w-48" />
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-slide-up" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Settings
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Configure your portfolio and trading preferences
          </p>
        </div>
        <Button onClick={saveSettings} disabled={saving} data-testid="save-settings-btn">
          {saving ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Settings
        </Button>
      </div>

      {/* Trading Mode */}
      <Card data-testid="trading-mode-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            <CardTitle className="text-sm uppercase tracking-widest font-medium" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Trading Mode
            </CardTitle>
          </div>
          <CardDescription>
            Switch between paper trading and live trading
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-md">
            <div className="flex items-center gap-3">
              {settings.is_paper_trading ? (
                <AlertTriangle className="w-6 h-6 text-warning" />
              ) : (
                <div className="w-3 h-3 bg-success rounded-full animate-pulse" />
              )}
              <div>
                <p className="font-medium">
                  {settings.is_paper_trading ? "Paper Trading Mode" : "Live Trading Mode"}
                </p>
                <p className="text-sm text-muted-foreground">
                  {settings.is_paper_trading 
                    ? "Simulated trades - no real money involved" 
                    : "Real trades with actual funds"}
                </p>
              </div>
            </div>
            <Switch
              checked={settings.is_paper_trading}
              onCheckedChange={(checked) => handleChange("is_paper_trading", checked)}
              data-testid="trading-mode-switch"
            />
          </div>

          {!settings.is_paper_trading && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Live Trading Warning</AlertTitle>
              <AlertDescription>
                Live trading mode will execute real trades with real money. 
                Ensure you understand the risks before proceeding.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Risk Profile */}
      <Card data-testid="risk-profile-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            <CardTitle className="text-sm uppercase tracking-widest font-medium" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Risk Profile
            </CardTitle>
          </div>
          <CardDescription>
            Set your investment risk tolerance
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select 
            value={settings.risk_profile} 
            onValueChange={(value) => handleChange("risk_profile", value)}
          >
            <SelectTrigger data-testid="risk-profile-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {riskProfiles.map((profile) => (
                <SelectItem key={profile.value} value={profile.value}>
                  <div className="flex flex-col">
                    <span className="font-medium">{profile.label}</span>
                    <span className="text-xs text-muted-foreground">{profile.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="grid grid-cols-4 gap-2">
            {riskProfiles.map((profile) => (
              <div
                key={profile.value}
                className={`p-3 rounded-md border text-center cursor-pointer transition-all ${
                  settings.risk_profile === profile.value
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-muted-foreground"
                }`}
                onClick={() => handleChange("risk_profile", profile.value)}
              >
                <p className="text-xs font-medium">{profile.label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Portfolio Settings */}
      <Card data-testid="portfolio-settings-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <SettingsIcon className="w-5 h-5 text-primary" />
            <CardTitle className="text-sm uppercase tracking-widest font-medium" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Portfolio Configuration
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Starting Balance (£)</Label>
            <Input
              type="number"
              value={settings.starting_balance}
              onChange={(e) => handleChange("starting_balance", parseFloat(e.target.value))}
              min="1000"
              step="1000"
              data-testid="starting-balance-input"
            />
            <p className="text-xs text-muted-foreground">
              Initial capital for paper trading
            </p>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Target ROI</Label>
              <Badge variant="outline">{settings.target_roi}%</Badge>
            </div>
            <Slider
              value={[settings.target_roi]}
              onValueChange={([value]) => handleChange("target_roi", value)}
              min={5}
              max={50}
              step={5}
              data-testid="target-roi-slider"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>5% (Conservative)</span>
              <span>50% (Very Aggressive)</span>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-md">
            <div>
              <p className="font-medium">Auto Rebalance</p>
              <p className="text-sm text-muted-foreground">
                Automatically rebalance portfolio based on AI suggestions
              </p>
            </div>
            <Switch
              checked={settings.auto_rebalance}
              onCheckedChange={(checked) => handleChange("auto_rebalance", checked)}
              data-testid="auto-rebalance-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card data-testid="notifications-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" />
            <CardTitle className="text-sm uppercase tracking-widest font-medium" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Notifications
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-md">
            <div>
              <p className="font-medium">Enable Notifications</p>
              <p className="text-sm text-muted-foreground">
                Receive alerts for market regime changes and AI suggestions
              </p>
            </div>
            <Switch
              checked={settings.notifications_enabled}
              onCheckedChange={(checked) => handleChange("notifications_enabled", checked)}
              data-testid="notifications-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Price Surge Alerts */}
      <Card className="border-green-500/30" data-testid="price-alerts-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-500" />
            <CardTitle className="text-sm uppercase tracking-widest font-medium" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Price Surge Alerts
            </CardTitle>
            <Badge className="bg-green-500 text-white text-[10px]">NEW</Badge>
          </div>
          <CardDescription>
            Get notified when your holdings move significantly
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-green-500/10 rounded-md border border-green-500/20">
            <div>
              <p className="font-medium">Monitoring {alertSettings.monitored_symbols} symbols</p>
              <p className="text-sm text-muted-foreground">
                Checking every {alertSettings.check_interval} seconds
              </p>
            </div>
            <Badge variant="outline" className="text-green-500 border-green-500">
              Active
            </Badge>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Alert Threshold</Label>
              <span className="text-sm font-medium text-green-500">±{alertSettings.surge_threshold}%</span>
            </div>
            <Slider
              value={[alertSettings.surge_threshold]}
              min={1}
              max={20}
              step={0.5}
              onValueChange={(value) => updateAlertSettings(value[0])}
              className="w-full"
              data-testid="alert-threshold-slider"
            />
            <p className="text-xs text-muted-foreground">
              You'll be alerted when any holding moves ±{alertSettings.surge_threshold}% from its last recorded price
            </p>
          </div>
          
          <div className="pt-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={clearAllAlerts}
              disabled={clearingAlerts}
              className="text-destructive hover:text-destructive"
              data-testid="clear-alerts-btn"
            >
              {clearingAlerts ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Clear All Alerts
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Email Briefing */}
      <Card className="border-amber-500/30" data-testid="email-briefing-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-amber-500" />
            <CardTitle className="text-sm uppercase tracking-widest font-medium" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Morning Briefing Email
            </CardTitle>
            <Badge className="bg-amber-500 text-white text-[10px]">NEW</Badge>
          </div>
          <CardDescription>
            Receive daily market intelligence at 7 AM UK time
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable Toggle */}
          <div className="flex items-center justify-between p-4 bg-amber-500/10 rounded-md border border-amber-500/20">
            <div>
              <p className="font-medium">Enable Daily Briefing</p>
              <p className="text-sm text-muted-foreground">
                Get Scout's picks, market regime, and action items
              </p>
            </div>
            <Switch
              checked={briefingSettings.enabled}
              onCheckedChange={(checked) => setBriefingSettings(prev => ({ ...prev, enabled: checked }))}
              data-testid="briefing-enabled-switch"
            />
          </div>
          
          {/* Email Address */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email Address
            </Label>
            <Input
              type="email"
              value={briefingSettings.recipient_email}
              onChange={(e) => setBriefingSettings(prev => ({ ...prev, recipient_email: e.target.value }))}
              placeholder="your@email.com"
              data-testid="briefing-email-input"
            />
          </div>
          
          {/* Delivery Time */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Delivery Time (UK)
            </Label>
            <Select 
              value={briefingSettings.send_time_utc.toString()} 
              onValueChange={(value) => setBriefingSettings(prev => ({ ...prev, send_time_utc: parseInt(value) }))}
            >
              <SelectTrigger data-testid="briefing-time-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="6">6:00 AM</SelectItem>
                <SelectItem value="7">7:00 AM (Recommended)</SelectItem>
                <SelectItem value="8">8:00 AM</SelectItem>
                <SelectItem value="9">9:00 AM</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Briefings are sent before UK markets open (8:00 AM)
            </p>
          </div>
          
          {/* Briefing Contents */}
          <div className="p-4 bg-muted/30 rounded-md">
            <p className="text-sm font-medium mb-2">Your briefing will include:</p>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-green-500" />
                Market Regime status (BULL/BEAR/VOLATILE)
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-green-500" />
                Scout's top 3 stock picks with momentum scores
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-green-500" />
                Macro outlook (economic, policy, geopolitical)
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-green-500" />
                S&P 500 overnight movement
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-green-500" />
                Daily action items
              </li>
            </ul>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={previewBriefing}
              className="flex-1"
              data-testid="preview-briefing-btn"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Preview Briefing
            </Button>
            <Button 
              onClick={sendTestBriefing}
              disabled={sendingTest || !briefingSettings.recipient_email}
              className="flex-1 bg-amber-500 hover:bg-amber-600"
              data-testid="send-test-briefing-btn"
            >
              {sendingTest ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Test Email
            </Button>
          </div>
          
          {/* API Key Notice */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Resend API Key Required</AlertTitle>
            <AlertDescription>
              To send emails, add your Resend API key to the backend. 
              Get a free key at <a href="https://resend.com" target="_blank" rel="noreferrer" className="text-amber-500 underline">resend.com</a>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-destructive/30" data-testid="danger-zone-card">
        <CardHeader>
          <CardTitle className="text-sm uppercase tracking-widest font-medium text-destructive" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Danger Zone
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <div>
              <p className="font-medium">Reset Portfolio</p>
              <p className="text-sm text-muted-foreground">
                Clear all holdings and trade history
              </p>
            </div>
            <Button 
              variant="destructive" 
              size="sm"
              onClick={resetPortfolio}
              data-testid="reset-portfolio-btn"
            >
              Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info */}
      <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-md">
        <Info className="w-5 h-5 text-muted-foreground mt-0.5" />
        <div>
          <p className="text-sm text-muted-foreground">
            Videra Wealth is designed for UK investors. All stocks shown are available 
            through Hargreaves Lansdown. This platform is for informational purposes 
            only and does not constitute financial advice.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Settings;
